import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { RfidBadgeOrderStatus } from '@delmonicos/shared-sdk/libs/cpms/src/libs/user-service-lib/src/wrapper/enums/rfid-badge-order-status.enum';
import { RfidBadgeOrderModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/user-service-lib/src/wrapper/outputs/rfid-badge-orders/rfid-badge-order.model';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useEmspBoGetRfidBadgeOrderQuery } from '@app/core/service/emsp-bo-rfid-badge-order.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

import { EmspBoRfidBadgeOrderFulfillController } from '../emsp-bo-rfid-badge-order-fulfill/emsp-bo-rfid-badge-order-fulfill.controller';
import { EmspBoRfidBadgeOrderContextProvider } from '../emsp-bo-rfid-badge-order.context';
import { EmspBoRfidBadgeOrderDetailsView } from './emsp-bo-rfid-badge-order-details.view';

interface EmspBoRfidBadgeOrderDetailsControllerProps {
  resource: RfidBadgeOrderModel;
}

const EmspBoRfidBadgeOrderDetailsController = ({
  resource,
}: EmspBoRfidBadgeOrderDetailsControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionFulfillIsDialogOpen, setActionFulfillIsDialogOpen] =
    useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  // Nothing to do — resource is passed as prop from EmspBoRfidBadgeOrderDetailsPage

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.orderNumber);
  }, [resource.orderNumber, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      resource.status === RfidBadgeOrderStatus.PAID &&
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.FULFILL_RFID_BADGE_ORDER,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="fulfill"
          icon="check-circle"
          onClick={() => setActionFulfillIsDialogOpen(true)}
        />,
      );
    }

    setPageAction(actions);
  }, [resource.status, setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <EmspBoRfidBadgeOrderContextProvider data={resource}>
      <EmspBoRfidBadgeOrderDetailsView />
      <Dialog
        open={actionFulfillIsDialogOpen}
        onClose={() => setActionFulfillIsDialogOpen(false)}>
        <Dialog.Header
          title={t('$.emsp-bo-rfid-badge-order-fulfill.title')}
          icon="check-circle"
          onClose={() => setActionFulfillIsDialogOpen(false)}
        />
        <Dialog.Content>
          <EmspBoRfidBadgeOrderFulfillController
            onCancel={() => setActionFulfillIsDialogOpen(false)}
            onSuccess={() => setActionFulfillIsDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
    </EmspBoRfidBadgeOrderContextProvider>
  );
};

export const EmspBoRfidBadgeOrderDetailsPage = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const params = useParams();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useEmspBoGetRfidBadgeOrderQuery({
    rfidBadgeOrderId: params.id ?? '',
  });

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component } = withProtectedResource<
    RfidBadgeOrderModel,
    object,
    RfidBadgeOrderModel
  >(EmspBoRfidBadgeOrderDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.READ_RFID_BADGE_ORDER,
      }),
  })({});

  return Component;
};
